<template>
  <v-dialog v-model="dialog" max-width="560" scrollable>
    <template v-slot:activator="{ on }">
      <v-list-item v-on="on">
        <v-list-item-icon>
          <v-icon size="19">mdi-link-variant</v-icon>
        </v-list-item-icon>
        <v-list-item-title>Create payment collection link</v-list-item-title>
      </v-list-item>
    </template>

    <v-card class="payment-link-dialog">
      <div class="payment-link-dialog__header">
        <div class="payment-link-dialog__icon" aria-hidden="true">
          <v-icon color="primary">mdi-link-variant</v-icon>
        </div>
        <div>
          <h2 class="payment-link-dialog__title">Share payment link</h2>
          <p class="payment-link-dialog__subtitle">
            Send this secure link to {{ customer.fatherName || customer.name || "the parent" }}.
          </p>
        </div>
        <v-btn icon aria-label="Close payment link dialog" @click="dialog = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-divider />

      <v-card-text class="payment-link-dialog__body">
        <v-select
          v-model="selectedCategory"
          :items="categories"
          :loading="isLoadingCategories"
          return-object
          item-text="name"
          outlined
          dense
          label="Payment category"
          hint="The selected category determines where the payment method is stored."
          persistent-hint
          class="mb-5"
          @change="createToken"
        >
          <template v-slot:selection="{ item }"><span class="payment-link-dialog__category-name">{{ item.name }}</span><PaymentProviderChip :provider="providerFor(item)" /></template>
          <template v-slot:item="{ item }"><span class="payment-link-dialog__category-name">{{ item.name }}</span><PaymentProviderChip :provider="providerFor(item)" /></template>
        </v-select>
        <div v-if="selectedCategory && selectedCategory.MerchantAccount" class="payment-link-dialog__routing">
          <v-icon size="18" color="primary">mdi-bank-transfer</v-icon>
          <div><strong>{{ selectedCategory.MerchantAccount.name }}</strong><span>Payment methods from this link are stored with this merchant account.</span></div>
          <PaymentProviderChip :provider="providerFor(selectedCategory)" :x-small="false" />
        </div>
        <label class="payment-link-dialog__label" for="payment-collection-url">
          Payment collection link
        </label>
        <div class="payment-link-dialog__link-box">
          <span id="payment-collection-url" class="payment-link-dialog__url">{{ url }}</span>
          <v-btn
            color="primary"
            depressed
            :disabled="isCreatingLink"
            @click="copyLink"
          >
            <v-icon left size="19">mdi-content-copy</v-icon>
            Copy link
          </v-btn>
        </div>
        <div class="payment-link-dialog__hint">
          <v-icon size="17" color="success">mdi-shield-check-outline</v-icon>
          {{ paymentMethodHint }}
        </div>

        <div class="payment-link-dialog__section-title">Send directly</div>
        <div class="payment-link-dialog__actions">
          <v-dialog v-model="whatsAppDialog" max-width="450">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                outlined
                color="success"
                class="payment-link-dialog__share-button"
                v-bind="attrs"
                v-on="on"
                :disabled="isCreatingLink"
              >
                <v-icon left>mdi-whatsapp</v-icon>
                WhatsApp
              </v-btn>
            </template>
            <v-card>
              <ModelTitle title="WhatsApp message" @close="whatsAppDialog = false" />
              <v-card-text class="pt-5">
                <v-text-field
                  class="centered-input"
                  type="tel"
                  outlined
                  label="WhatsApp number"
                  v-model="cellNumber"
                />
                <v-textarea
                  autofocus
                  outlined
                  label="Message"
                  v-model="message"
                />
              </v-card-text>
              <v-divider />
              <v-card-actions>
                <v-spacer />
                <v-btn text @click="whatsAppDialog = false">Cancel</v-btn>
                <v-btn color="primary" depressed @click="sendWhatsAppMessage">Send</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>

          <v-dialog v-model="textMessagesDialog" max-width="400">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                outlined
                color="primary"
                class="payment-link-dialog__share-button"
                v-bind="attrs"
                v-on="on"
                :disabled="isCreatingLink"
              >
                <v-icon left>mdi-message-text-outline</v-icon>
                Text message
              </v-btn>
            </template>
            <v-card>
              <ModelTitle title="Send text message" @close="textMessagesDialog = false" />
              <v-card-text class="pt-5">
                <v-text-field
                  class="centered-input"
                  type="tel"
                  outlined
                  autofocus
                  label="Cell number"
                  v-model="cellNumber"
                />
              </v-card-text>
              <v-divider />
              <v-card-actions>
                <v-spacer />
                <v-btn text @click="textMessagesDialog = false">Cancel</v-btn>
                <v-btn color="primary" depressed @click="sendTextMessage">Send</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>

          <v-btn
            v-if="showShare"
            outlined
            color="primary"
            class="payment-link-dialog__share-button"
            :disabled="isCreatingLink"
            @click="shareLink"
          >
            <v-icon left>mdi-share-variant-outline</v-icon>
            More options
          </v-btn>
        </div>
      </v-card-text>

      <v-divider />
      <v-card-actions class="payment-link-dialog__footer">
        <v-spacer />
        <v-btn text color="primary" @click="dialog = false">Done</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import copy from "copy-to-clipboard";
import { mapGetters } from "vuex";
import customerService from "@/modules/Customer/service.js";
import donationCategoryService from "@/modules/DonationCategory/service.js";

export default {
  props: ["id", "customer"],
  name: "create-payment-link",
  data() {
    return {
      whatsAppDialog: false,
      cellNumber: null,
      textMessagesDialog: false,
      token: null,
      message: null,
      showShare: false,
      url: "Creating secure link…",
      dialog: false,
      categories: [],
      selectedCategory: null,
      isLoadingCategories: false,
    };
  },
  computed: {
    ...mapGetters("global", ["currentBusiness"]),
    isCreatingLink() {
      return !this.token || !this.selectedCategory;
    },
    paymentMethodHint() {
      return this.providerFor(this.selectedCategory) === "STRIPE"
        ? "The parent can securely add a credit or debit card from any device."
        : "The parent can securely add a card or bank account from any device."
    },
  },
  watch: {
    textMessagesDialog(val) {
      if (val) this.cellNumber = this.customer.fatherPhoneNumber;
    },
    whatsAppDialog(val) {
      if (val) this.cellNumber = this.customer.fatherPhoneNumber;
    },
    dialog(val) {
      if (val) this.loadCategories();
    },
  },
  created() {
    this.showShare = !!navigator.share;
  },
  methods: {
    providerFor(category) {
      return category && category.MerchantAccount && category.MerchantAccount.provider
        ? category.MerchantAccount.provider
        : "PAYCOMPASS";
    },
    async loadCategories() {
      this.token = null;
      this.url = "Select a payment category…";
      this.isLoadingCategories = true;
      try {
        const response = await donationCategoryService.getAll();
        this.categories = response.data || [];
        this.selectedCategory = this.categories.find(category => category.id === this.currentBusiness.DefaultSchoolFeeId) || this.categories[0] || null;
        if (this.selectedCategory) await this.createToken();
      } finally {
        this.isLoadingCategories = false;
      }
    },
    sendWhatsAppMessage() {
      const message = this.message || "";
      const before = message.split("{{");
      const after = message.split("}}");
      const startIndex = message.indexOf("{{");
      let newValue = message;

      if (startIndex !== -1) {
        newValue = `${encodeURIComponent(before[0].trim())} ${this.url} ${encodeURIComponent((after[1] || "").trim())}`;
      }

      window.open(`https://api.whatsapp.com/send?phone=${this.cellNumber}&text=${newValue}`, "_blank");
      this.whatsAppDialog = false;
      this.dialog = false;
    },
    sendTextMessage() {
      const obj = {
        phoneNumber: this.cellNumber,
        url: this.url,
        customerName: this.customer.name,
      };

      customerService.publicSendTextMessage(obj).then(() => {
        this.$swal.mixin({
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
        }).fire({
          icon: "success",
          title: "Message sent",
        });
      });

      this.textMessagesDialog = false;
      this.dialog = false;
    },
    async createToken() {
      if (!this.selectedCategory) return;
      this.token = null;
      this.url = "Creating secure link…";
      const result = await customerService.createLink({ CustomerId: this.id, DonationCategoryId: this.selectedCategory.id });
      this.token = result.data.token;
      this.url = `${process.env.VUE_APP_FE_URL}/p/${this.token}`;
    },
    shareLink() {
      if (navigator.share) {
        navigator.share({
          title: this.currentBusiness.name,
          text: `Secure payment link from ${this.currentBusiness.name}`,
          url: this.url,
        }).catch(() => {});
      }
    },
    copyLink() {
      copy(this.url);
      this.$swal.mixin({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 2500,
      }).fire({
        icon: "success",
        title: "Payment link copied",
      });
    },
  },
};
</script>

<style scoped>
.payment-link-dialog {
  border-radius: 16px;
}

.payment-link-dialog__header {
  align-items: flex-start;
  display: grid;
  gap: 14px;
  grid-template-columns: 44px 1fr auto;
  padding: 24px;
}
.payment-link-dialog__category-name { min-width: 0; margin-right: 10px; overflow: hidden; flex: 1; text-overflow: ellipsis; white-space: nowrap; }
.payment-link-dialog__routing { display: flex; gap: 10px; margin: -4px 0 20px; padding: 12px; align-items: center; border: 1px solid #dfe9e1; border-radius: 10px; background: #f5faf6; }
.payment-link-dialog__routing > div { display: flex; min-width: 0; flex: 1; flex-direction: column; }
.payment-link-dialog__routing strong { color: #35433a; font-size: 12px; }
.payment-link-dialog__routing span { color: #7b887e; font-size: 10px; }

.payment-link-dialog__icon {
  align-items: center;
  background: #edf7ee;
  border-radius: 12px;
  display: flex;
  height: 44px;
  justify-content: center;
  width: 44px;
}

.payment-link-dialog__title {
  color: #1f2937;
  font-size: 20px;
  line-height: 1.35;
  margin: 0;
}

.payment-link-dialog__subtitle {
  color: #667085;
  font-size: 14px;
  margin: 4px 0 0;
}

.payment-link-dialog__body {
  /* The floating outlined label extends above the field; keep it clear of the
     scrollable card-text boundary and the header divider. */
  padding: 32px 24px 24px !important;
}

.payment-link-dialog__label,
.payment-link-dialog__section-title {
  color: #344054;
  display: block;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 8px;
}

.payment-link-dialog__section-title {
  margin-top: 24px;
}

.payment-link-dialog__link-box {
  align-items: center;
  background: #f8fafc;
  border: 1px solid #dfe5ec;
  border-radius: 10px;
  display: flex;
  gap: 12px;
  padding: 8px 8px 8px 14px;
}

.payment-link-dialog__url {
  color: #344054;
  flex: 1;
  font-size: 14px;
  line-height: 1.45;
  min-width: 0;
  overflow-wrap: anywhere;
}

.payment-link-dialog__hint {
  align-items: flex-start;
  color: #667085;
  display: flex;
  font-size: 12px;
  gap: 6px;
  margin-top: 10px;
}

.payment-link-dialog__actions {
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.payment-link-dialog__share-button {
  justify-content: flex-start;
  min-height: 44px;
}

.payment-link-dialog__footer {
  padding: 10px 16px;
}

@media (max-width: 600px) {
  .payment-link-dialog__header {
    gap: 10px;
    grid-template-columns: 40px 1fr auto;
    padding: 18px 16px;
  }

  .payment-link-dialog__icon {
    height: 40px;
    width: 40px;
  }

  .payment-link-dialog__body {
    padding: 28px 16px 20px !important;
  }

  .payment-link-dialog__link-box {
    align-items: stretch;
    flex-direction: column;
    padding: 12px;
  }

  .payment-link-dialog__actions {
    grid-template-columns: 1fr;
  }
}
</style>
